import * as vp from 'vopay';
import { CustomAppendNoResultErrorMessage } from 'vopay';
import { ChangeEvent, JSX, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { useClientData } from '@/providers/ClientData.provider';
import { SingleCollection, ItemProps, FinancialInstitutionsType } from './types';
import useFetchInteracEmail from '@/hooks/useApi/useFetchInteracEmail';
import InteracInfoContext from '@/components/Accounts/AccountCreation/Interac/InteracInfoContext';
import SingleCollectionBankInfoContext from './SingleCollectionBankInfoContext';
import useFetchClientAccountList from '@/hooks/useApi/useFetchClientAccountList';

import styles from './SingleCollectionForm.module.scss';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import useFetchClientBankAccounts from '@/hooks/useApi/useFetchClientBankAccounts';
import { useDataFetchIndicator } from '@/hooks/';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';

type SelectOption = vp.SelectOption<vp.ClientAccount>;
interface InteracEmail {
  RecipientPhoneNumber: string;
  RecipientName: string;
  RecipientEmailAddress: string;
  Question: string;
  Answer: string;
  ClientReferenceNumber: string;
  Token?: string;
}

export type SingleCollectionRows = {
  label: vp.SingleCollectionLabel;
  id: vp.SingleCollectionId;
  require: (ctx: SingleCollection) => boolean;
  preset: keyof vp.TextFieldPresets | undefined;
  type: 'input' | 'select' | 'singleDate' | 'checkbox' | 'multiSelector' | 'toggle' | 'radio';
  placeholder: string;
  tooltipMessage?: string;
  allowNewOption?: boolean;
  customOptions?: SelectOption[];
  renderOption?: (o: SelectOption, args: { focused: boolean }) => React.ReactNode;
  renderDropdownHeader?: (onClick: (value: string) => void, permissions: vp.ApiClientPermissions) => React.ReactNode;
  visible: (ctx: SingleCollection) => boolean;
  customAppendNoResultErrorMessage?: CustomAppendNoResultErrorMessage;
  maxLength?: number;
  disableFiltering?: (ctx: SingleCollection) => boolean;
  searchCallback?: (term: string) => void;
  onChangeCallBack?: (
    event: ChangeEvent<HTMLInputElement>,
    ctx: SingleCollection,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
    setFieldTouched: (field: string, isTouched?: boolean | undefined, shouldValidate?: boolean | undefined) => void,
  ) => void;
  SelectLabel?: (ctx: SingleCollection) => string | undefined;
  getSelectOptions?: (ctx: SingleCollection) => { [key: string]: string };
  getMultiSelectOptions?: (ctx: SingleCollection) => ItemProps[];
  getRadioOptions?: (ctx: SingleCollection) => { [key: string]: string | JSX.Element };
  getErrorMessage?: (ctx: SingleCollection) => string | string[] | null;
  getGoogleCountries?: (ctx: SingleCollection) => { [key: string]: string };
  handleGoogleResults?: (ctx: SingleCollection, addressComponents: vp.AddressComponents) => void;
  disabled: (ctx: SingleCollection) => boolean;
  getCheckboxOptions?: () => {
    label: string;
    checked: boolean;
    isReadOnly: boolean;
    isDisabled: boolean;
  }[];
  getSubElement?: (ctx: SingleCollection) => JSX.Element | undefined;
  onBlur?: () => void;
};

export type Ctx = SingleCollection;
export type TransactionDetail = SingleCollectionRows;
export type ContactDetail = SingleCollectionRows;
export type AddressDetail = SingleCollectionRows;
export type BankDetail = SingleCollectionRows;
export type InteracDetail = SingleCollectionRows;
export type RequestDetail = SingleCollectionRows;

export const buildSingleCollectionRows = (
  moreInfoNeeded: boolean,
  FinancialInstitutions: FinancialInstitutionsType,
  financialInstitutionBranches: FinancialInstitutionsType,
  PaymentMethod: string,
  currency: string,
  onClientAccountChange: (ClientAccount: vp.ClientAccount) => void,
  contactCreditCards: {
    [token: string]: vp.ClientCreditCardLists;
  },
  accountHolderName?: string,
): [TransactionDetail[], ContactDetail[], AddressDetail[], BankDetail[], InteracDetail[], RequestDetail[]] => {
  const {
    response: {
      TransactionLabelOverrideEnabled,
      ClientAccountsEnabled,
      USDEFTSendEnabled,
      Country,
      KYCLightEnabled,
      LoginUserName,
      ACHFundingProcessor,
    },
  } = useClientData();

  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const InteracCtx = useContext(InteracInfoContext);
  const SingleCollectionBankInfoCtx = useContext(SingleCollectionBankInfoContext);
  const { PayLinkEnabled } = useLockedFeatures();

  const [, , , fetchEmails] = useFetchInteracEmail({});
  const [, , , fetchClientBankAccounts] = useFetchClientBankAccounts({});
  const [, , , getClientAccountList] = useFetchClientAccountList({});
  const [creditCardOptions, setCreditCardOptions] = useState({});
  const [interacEmailOptions, setInteracEmailOptions] = useState({});
  const [clientAccountOptions, setClientAccountOptions] = useState<SelectOption[]>([]);
  const [bankAccountOptions, setBankAccountOptions] = useState({});
  const [bankInfo, setBankInfo] = useState<vp.ClientBankAccountsLists>();
  const [clientAccountDetail, setClientAccountDetail] = useState<vp.ClientAccount>();

  const [shouldInitializeBankList, setShouldInitializeBankList] = useState(true);
  const [shouldInitializeEmailList, setShouldInitializeEmailList] = useState(true);
  const [shouldInitializeClientList, setShouldInitializeClientList] = useState(true);
  const [originalBankList, setOriginalBankList] = useState({});
  const [originalEmailList, setOriginalEmailList] = useState({});
  const [originalClientList, setOriginalClientList] = useState<SelectOption[]>([]);

  const INTERAC = 'Interac e-Transfer Money Request';
  const BANK = 'Bank Account';
  const CREDIT_CARD = 'Credit Card';
  const GOOGLE_PAY = 'Google Pay';
  const PROVIDE = 'provide';
  const INDIVIDUAL = 'Individual';
  const PHONE = 'phone';
  const EMAIL = 'email';
  const BUSINESS = 'Business';
  const CAD = 'CAD';
  const USD = 'USD';
  const CA = 'CA';
  const US = 'US';

  const etransferEmailConnectionOption: { [key: string]: string } = {
    email: 'Email Address',
  };

  const CurrencyOptions = {
    ...vp.CURRENCY_SELECT_OPTIONS,
  };

  const CADOnly: { [key: string]: string } = {
    CAD: 'CAD',
  };

  const FrequencyOptions = {
    ...vp.FREQUENCY_OPTIONS,
  };

  const PaymentOptions: { [key: string]: string } = {
    'Bank Account': 'Bank Account',
    'Interac e-Transfer Money Request': '<i>Interac</i> Money Request<sup>&reg;</sup>',
    'Credit Card': 'Credit Card',
    // 'Google Pay': 'Google Pay', ToDo: Enable Google Pay later
  };

  const BankingPaymentRailOptions: { [key: string]: string } = {
    eft: 'Electronic Funds Transfer',
  };

  const USPaymentOptions: { [key: string]: string } = {
    'Bank Account': 'Bank Account',
    'Credit Card': 'Credit Card',
    // 'Google Pay': 'Google Pay', ToDo: Enable Google Pay later
  };

  const USBankingPaymentRailOptions: { [key: string]: string } = {
    ach: 'Automated Clearing House',
  };

  const ScheduleTypeOptions: { [key: string]: string } = {
    single: 'One Time',
    recurring: 'Recurring',
  };

  const EndingOptions: { [key: string]: string } = {
    'End Date': 'End Date',
    Payments: 'Payments',
  };

  const SemiMonthlyDateOptions = {
    SemiMonthFirstAndFifteenth: '1st and 15th Day of the Month',
    SemiMonthFifteenthAndLast: '15th and Last Day of the Month',
  };

  const AccountConnectionMethods: ItemProps[] = [
    {
      label: 'Online',
      value: 'Online',
      checked: true,
      isReadOnly: false,
      isDisabled: false,
      isLocked: false,
    },
    {
      label: 'Manual',
      value: 'Manual',
      checked: true,
      isReadOnly: false,
      isDisabled: false,
    },
  ];

  const noResultErrorMessage: CustomAppendNoResultErrorMessage = {
    label: 'is not in your contact list. Please select a current contact or add a new one.',
    style: { color: 'red' },
  };

  const getNotificationMethodOptions = (defaultMethod: vp.ElinxNotificationMethod) => {
    return {
      email: 'Email Address',
      sms: 'SMS',
      ...(defaultMethod === 'whatsapp' && { whatsapp: 'WhatsApp' }),
    };
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (clientAccountDetail) {
      onClientAccountChange(clientAccountDetail);
    }
  }, [clientAccountDetail]);

  async function init() {
    if (ClientAccountsEnabled === '1') {
      handleClientAccountSearch('');
      setShouldInitializeClientList(true);
    } else {
      handleBankAccountsSearch(accountHolderName || '');
      setShouldInitializeBankList(true);
      handleEmailSearch(accountHolderName || '');
      setShouldInitializeEmailList(true);
    }
  }

  const [contactClientAccounts, setContactClientAccounts] = useState({});
  const [contactBankAccounts, setContactBankAccounts] = useState({});
  const [contactEmail, setContactEmail] = useState({});
  const { userSettings } = useUserSettingsProvider();

  async function handleClientAccountSearch(query: string) {
    setDataFetchingInfo(true, 'loading some data', 'fullScreen');
    const res: vp.ClientAccountListResponse = await getClientAccountList({
      ClientType: '',
      Limit: 100,
      SearchText: query === '' ? undefined : query,
    });
    setDataFetchingInfo(false);

    if (res.Success) {
      setContactClientAccounts(res.ClientAccounts);
    } else {
      setContactClientAccounts({});
    }
  }

  async function handleBankAccountsSearch(query: string) {
    setDataFetchingInfo(true, 'loading some data', 'fullScreen');

    const res: vp.ClientBankAccountsResponse = await fetchClientBankAccounts({
      PortalUser: LoginUserName,
      ClientControlled: false,
      SearchText: query,
      Token: '',
      Limit: 100,
      Timezone: userSettings.timezone,
    });

    setDataFetchingInfo(false);

    if (res.Success) {
      setContactBankAccounts(res.BankAccounts);
    } else {
      setContactBankAccounts({});
    }
  }

  async function handleEmailSearch(query: string) {
    setDataFetchingInfo(true, 'loading some data', 'fullScreen');
    const res: vp.InteracEmailResponse = await fetchEmails({
      ClientControlled: false,
      SearchText: query,
      Limit: 100,
    });
    setDataFetchingInfo(false);

    if (res.Success) {
      setContactEmail(res.InteracEmails);
    } else {
      setContactEmail({});
    }
  }

  useEffect(() => {
    setClientAccountOptions(buildClientAccountOptions(Object.values(contactClientAccounts)));
  }, [contactClientAccounts]);

  useEffect(() => {
    const bankAccountsArray = Object.values(contactBankAccounts || {}) as vp.ClientBankAccountsLists[];

    setBankAccountOptions(buildBankAccountOptions(bankAccountsArray.filter((data) => data.Currency === currency)));
  }, [contactBankAccounts, currency]);

  useEffect(() => {
    setCreditCardOptions(buildCreditCardsOptions(Object.values(contactCreditCards)));
  }, [contactCreditCards]);

  useEffect(() => {
    setInteracEmailOptions(buildInteracEmailsOptions(Object.values(contactEmail)));
  }, [contactEmail]);

  useEffect(() => {
    if (shouldInitializeBankList && Object.keys(bankAccountOptions).length > 0) {
      setOriginalBankList(bankAccountOptions);
      setShouldInitializeBankList(false);
    }
  }, [bankAccountOptions]);

  useEffect(() => {
    if (shouldInitializeEmailList && Object.keys(interacEmailOptions).length > 0) {
      setOriginalEmailList(interacEmailOptions);
      setShouldInitializeEmailList(false);
    }
  }, [interacEmailOptions]);

  useEffect(() => {
    if (shouldInitializeClientList && Object.keys(clientAccountOptions).length > 0) {
      setOriginalClientList(clientAccountOptions);
      setShouldInitializeClientList(false);
    }
  }, [clientAccountOptions]);

  useEffect(() => {
    if (shouldInitializeClientList && Object.keys(clientAccountOptions).length > 0) {
      setOriginalClientList(clientAccountOptions);
      setShouldInitializeClientList(false);
    }
  }, [clientAccountOptions]);

  function buildBankAccountOptions(lists: vp.ClientBankAccountsLists[]) {
    return lists.reduce(
      (accumulator, data) => ({
        ...accumulator,
        [data.Token]: `${data.Currency} ${data.AccountHolderName} ${getMaskedNumber(data.AccountNumber)} `,
      }),
      {},
    );
  }

  const transactionDetails: TransactionDetail[] = [
    {
      label: vp.SingleCollectionLabel.PaymentMethod,
      id: vp.SingleCollectionId.PaymentMethod,
      require: () => true,
      preset: 'text',
      type: 'select',
      placeholder: 'Please Select...',
      visible: () => true,
      getSelectOptions: (ctx: SingleCollection) => (ctx.Currency === 'CAD' ? PaymentOptions : USPaymentOptions),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.BankingPaymentRail,
      id: vp.SingleCollectionId.BankingPaymentRail,
      require: () => true,
      preset: 'text',
      type: 'select',
      placeholder: 'Please Select...',
      visible: (ctx: SingleCollection) => ctx.PaymentMethod === 'Bank Account',
      getSelectOptions: () => (Country === 'CA' ? BankingPaymentRailOptions : USBankingPaymentRailOptions),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.Currency,
      id: vp.SingleCollectionId.Currency,
      require: () => true,
      preset: 'text',
      type: 'select',
      placeholder: 'Please Select...',
      visible: () => true,
      getSelectOptions: () =>
        Country === US ? vp.US_CURRENCY_ONLY : USDEFTSendEnabled === '0' ? CADOnly : CurrencyOptions,
      disabled: () => false,
    },

    {
      label: vp.SingleCollectionLabel.Contact,
      id: vp.SingleCollectionId.Contact,
      require: () => true,
      preset: 'text',
      type: 'select',
      placeholder: 'Please Select...',
      visible: () => ClientAccountsEnabled === '0',
      disableFiltering: (ctx: SingleCollection) => isInterac(ctx) || isBank(ctx),
      customAppendNoResultErrorMessage: noResultErrorMessage,
      onBlur: () => {
        setBankAccountOptions(originalBankList);
      },
      SelectLabel: (ctx: SingleCollection) => {
        if (ctx.Contact === 'provide') return 'New, Provide';
        if (ctx.Contact === 'request') return 'New, Request';

        if (
          isInterac(ctx) &&
          InteracCtx.interacDetails &&
          ctx.Contact &&
          ctx.Contact !== PROVIDE &&
          ctx.Contact !== ''
        ) {
          return `${InteracCtx.interacDetails?.RecipientName || ''} (${
            InteracCtx.interacDetails?.RecipientEmailAddress || InteracCtx.interacDetails?.RecipientPhoneNumber || ''
          })`;
        }

        if (isBank(ctx) && ctx.Contact && ctx.Contact !== PROVIDE && ctx.Contact !== '') {
          return `${bankInfo?.Currency || ''}  ${bankInfo?.AccountHolderName || ''} ${getMaskedNumber(
            bankInfo?.AccountNumber || '',
          )}`;
        }
        return undefined;
      },
      onChangeCallBack: (event, ctx) => {
        const Contact = event.target.value;

        if (Contact === PROVIDE || (Contact !== '' && isInterac(ctx) && !vp.toBool(ClientAccountsEnabled))) {
          if (!vp.toBool(ClientAccountsEnabled) && Contact !== PROVIDE && Contact !== '' && isInterac(ctx)) {
            // move or use other list or all items
            const interacEmail = Object.values(contactEmail || {}).find(
              (interac) => (interac as InteracEmail).Token === Contact,
            ) as InteracEmail | undefined;

            if (interacEmail) {
              InteracCtx.updateInterac(false, false, {
                RecipientPhoneNumber: interacEmail?.RecipientPhoneNumber || '',
                RecipientName: interacEmail?.RecipientName || '',
                RecipientEmailAddress: interacEmail?.RecipientEmailAddress || '',
                Question: interacEmail?.Question || '',
                Answer: interacEmail?.Answer || '',
                ClientReferenceNumber: interacEmail?.ClientReferenceNumber,
              });
            }
          } else if (!vp.toBool(ClientAccountsEnabled) && Contact === PROVIDE && isInterac(ctx)) {
            if (
              InteracCtx.interacDetails?.RecipientPhoneNumber !== '' ||
              InteracCtx.interacDetails?.RecipientName !== '' ||
              InteracCtx.interacDetails?.RecipientEmailAddress !== '' ||
              InteracCtx.interacDetails?.Question !== '' ||
              InteracCtx.interacDetails?.Answer !== '' ||
              InteracCtx.interacDetails?.ClientReferenceNumber !== ''
            ) {
              InteracCtx.updateInterac(false, false, {
                RecipientPhoneNumber: '',
                RecipientName: '',
                RecipientEmailAddress: '',
                Question: '',
                Answer: '',
                ClientReferenceNumber: '',
              });
            }
          }
        }

        if (Contact === PROVIDE || (Contact !== '' && isBank(ctx) && !vp.toBool(ClientAccountsEnabled))) {
          const contactBankAccount = Object.values(contactBankAccounts || {}).find(
            (bank) => (bank as vp.ClientBankAccountsLists).Token === Contact,
          ) as vp.ClientBankAccountsLists | undefined;

          if (contactBankAccount) {
            setBankInfo(contactBankAccount || undefined);
            setBankAccountOptions(originalBankList);

            SingleCollectionBankInfoCtx.updateBankAccount({
              ...contactBankAccount,
            });
          }
        }
      },
      searchCallback: (value: string) => {
        if (PaymentMethod === INTERAC) {
          if (value === '') {
            setInteracEmailOptions(originalEmailList);
          } else {
            handleEmailSearch(value);
          }
        }

        if (PaymentMethod === BANK) {
          if (value === '') {
            setBankAccountOptions(originalBankList);
          } else {
            handleBankAccountsSearch(value);
          }
        }
      },
      getSelectOptions: (ctx: SingleCollection) => {
        switch (ctx.PaymentMethod) {
          case BANK:
            return bankAccountOptions;
          case CREDIT_CARD:
            return creditCardOptions;
          case GOOGLE_PAY:
            return {}; // GooglePay is only available for eLinx
          default:
            return interacEmailOptions;
        }
      },
      disabled: () => false,
      renderDropdownHeader: (onClick) => (
        <>
          {/* TODO: Enable the option to add new contact payment information for US credit card*/}
          {(Country !== US || PaymentMethod !== 'Credit Card') && (
            <div className={styles.dropdownHeader}>
              New
              <div>
                {PaymentMethod !== GOOGLE_PAY && (
                  <button type="button" onClick={() => onClick('provide')}>
                    + Provide new contact payment information
                  </button>
                )}

                {PayLinkEnabled && (
                  <button type="button" onClick={() => onClick('request')}>
                    + Request new contact payment information
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      ),
    },

    {
      label: vp.SingleCollectionLabel.ClientAccount,
      id: vp.SingleCollectionId.ClientAccount,
      require: () => true,
      preset: 'text',
      type: 'select',
      placeholder: 'Please Select...',
      visible: () => ClientAccountsEnabled === '1',
      customOptions: clientAccountOptions,
      renderOption: (o, args) => (
        <OptionLabel clientName={o.ClientName} currency={currency} balanceAccount={getBalanceAccount(o)} {...args} />
      ),
      disabled: () => false,
      disableFiltering: () => true,
      onChangeCallBack: (event, _ctx, setFieldValue, setFieldTouched) => {
        const ClientAccountId = event.target.value;
        const ClientAccount = Object.values(clientAccountOptions || {}).find(
          (account) => account.ClientAccountID === ClientAccountId,
        );

        if (ClientAccount) {
          setFieldTouched('IdentificationNumber', false);
          setFieldValue('IdentificationNumber', ClientAccount?.IdentificationNumber);
          setFieldValue('RecipientPhoneNumber', ClientAccount?.PhoneNumber || '');
          setFieldValue('ClientAccountPhoneNumber', ClientAccount?.PhoneNumber || '');
          setClientAccountDetail(ClientAccount);
        }
      },
      SelectLabel: () => {
        return clientAccountDetail?.ClientName;
      },
      searchCallback: (value: string) => {
        if (value === '') {
          setClientAccountOptions(originalClientList);
        } else {
          handleClientAccountSearch(value);
        }
      },
      getSubElement: (ctx: SingleCollection) => {
        const clientAccountBalance = Object.values(clientAccountDetail?.Balances || {}).find(
          (balance) => balance.Currency === ctx.Currency,
        );
        return ctx.ClientAccount !== '' && clientAccountDetail ? (
          <div className={styles.AvailableBalance}>
            {clientAccountBalance && (
              <span>
                Available Balance {ctx.Currency}{' '}
                {vp.formattedCurrency((clientAccountBalance.Balance - clientAccountBalance.PendingBalance).toString())}
              </span>
            )}
            {!clientAccountBalance && (
              <span>
                Available Balance {ctx.Currency} {vp.formattedCurrency('0')}
              </span>
            )}
          </div>
        ) : undefined;
      },
    },
    {
      label: vp.SingleCollectionLabel.Amount,
      id: vp.SingleCollectionId.Amount,
      require: () => true,
      preset: 'currency',
      type: 'input',
      placeholder: '0.00',
      visible: () => true,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.ContactType,
      id: vp.SingleCollectionId.ContactType,
      require: (ctx: SingleCollection) =>
        (isProvideNew(ctx) || isRequestNew(ctx)) && (!isInterac(ctx) || isRequestNew(ctx)) && !isCreditCard(ctx),
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: SingleCollection) =>
        (isProvideNew(ctx) || isRequestNew(ctx)) && (!isInterac(ctx) || isRequestNew(ctx)) && !isCreditCard(ctx),
      getSelectOptions: () => vp.ACCOUNT_TYPE_OPTIONS,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.ScheduleType,
      id: vp.SingleCollectionId.ScheduleType,
      require: (ctx: SingleCollection) =>
        isBank(ctx) && ((Country === CA && ctx.Currency === CAD) || (Country === US && ctx.Currency === USD)),
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: SingleCollection) =>
        isBank(ctx) && ((Country === CA && ctx.Currency === CAD) || (Country === US && ctx.Currency === USD)),
      getSelectOptions: () => ScheduleTypeOptions,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.TakeFromAccountOn,
      id: vp.SingleCollectionId.TakeFromAccountOn,
      require: (ctx: SingleCollection) =>
        isBank(ctx) &&
        ctx.ScheduleType === 'single' &&
        ((Country === CA && ctx.Currency === CAD) || (Country === US && ctx.Currency === USD)),
      preset: 'date',
      type: 'singleDate',
      placeholder: 'YYYY-MM-DD',
      visible: (ctx: SingleCollection) =>
        isBank(ctx) &&
        ctx.ScheduleType === 'single' &&
        ((Country === CA && ctx.Currency === CAD) || (Country === US && ctx.Currency === USD)),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.StartDate,
      id: vp.SingleCollectionId.StartDate,
      require: (ctx: SingleCollection) =>
        isBank(ctx) &&
        ctx.ScheduleType === 'recurring' &&
        ((Country === CA && ctx.Currency === CAD) || (Country === US && ctx.Currency === USD)),
      preset: 'date',
      type: 'singleDate',
      placeholder: 'YYYY-MM-DD',
      visible: (ctx: SingleCollection) =>
        isBank(ctx) &&
        ctx.ScheduleType === 'recurring' &&
        ((Country === CA && ctx.Currency === CAD) || (Country === US && ctx.Currency === USD)),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.Frequency,
      id: vp.SingleCollectionId.Frequency,
      require: (ctx: SingleCollection) =>
        isBank(ctx) &&
        ctx.ScheduleType === 'recurring' &&
        ((Country === CA && ctx.Currency === CAD) || (Country === US && ctx.Currency === USD)),
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: SingleCollection) =>
        isBank(ctx) &&
        ctx.ScheduleType === 'recurring' &&
        ((Country === CA && ctx.Currency === CAD) || (Country === US && ctx.Currency === USD)),
      getSelectOptions: () => FrequencyOptions,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.SemiMonthlyDates,
      id: vp.SingleCollectionId.SemiMonthlyDates,
      require: (ctx: SingleCollection) =>
        isBank(ctx) && ctx.ScheduleType === 'recurring' && ctx.Frequency === 'semi-monthly',
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: SingleCollection) =>
        isBank(ctx) && ctx.ScheduleType === 'recurring' && ctx.Frequency === 'semi-monthly',
      getSelectOptions: () => SemiMonthlyDateOptions,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.Ending,
      id: vp.SingleCollectionId.Ending,
      require: () => false,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: SingleCollection) =>
        isBank(ctx) &&
        ctx.ScheduleType === 'recurring' &&
        ((Country === CA && ctx.Currency === CAD) || (Country === US && ctx.Currency === USD)),
      getSelectOptions: () => EndingOptions,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.EndDate,
      id: vp.SingleCollectionId.EndDate,
      require: (ctx: SingleCollection) => isBank(ctx) && ctx.ScheduleType === 'recurring' && ctx.Ending === 'End Date',
      preset: 'date',
      type: 'singleDate',
      placeholder: 'YYYY-MM-DD',
      visible: (ctx: SingleCollection) => isBank(ctx) && ctx.ScheduleType === 'recurring' && ctx.Ending === 'End Date',
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.EndingAfterPayments,
      id: vp.SingleCollectionId.EndingAfterPayments,
      require: (ctx: SingleCollection) =>
        ctx.Ending === 'Payments' &&
        ((Country === CA && ctx.Currency === CAD) || (Country === US && ctx.Currency === USD)),
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) =>
        ctx.Ending === 'Payments' &&
        ((Country === CA && ctx.Currency === CAD) || (Country === US && ctx.Currency === USD)),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.IdentificationNumber,
      id: vp.SingleCollectionId.IdentificationNumber,
      require: (ctx: SingleCollection) =>
        ctx.PaymentMethod === 'Bank Account' &&
        ctx.BankingPaymentRail === 'ach' &&
        !vp.toBool(KYCLightEnabled) &&
        (ctx.Contact !== 'new' || vp.toBool(ClientAccountsEnabled)) &&
        ACHFundingProcessor === 'fiserv',
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) =>
        ctx.PaymentMethod === 'Bank Account' &&
        ctx.BankingPaymentRail === 'ach' &&
        !vp.toBool(KYCLightEnabled) &&
        (ctx.Contact !== 'new' || vp.toBool(ClientAccountsEnabled)) &&
        ACHFundingProcessor === 'fiserv',
      getSelectOptions: () => ({}),
      disabled: () => false,
    },

    {
      label: vp.SingleCollectionLabel.Description,
      id: vp.SingleCollectionId.Description,
      require: (ctx: SingleCollection) =>
        isBank(ctx) &&
        (ctx.ScheduleType === 'recurring' ||
          (ctx.TakeFromAccountOn !== '' &&
            !vp.IsTodayDate(moment(Number(ctx.TakeFromAccountOn)).format('YYYY-MM-DD')))) &&
        ((Country === CA && ctx.Currency === CAD) || (Country === US && ctx.Currency === USD)),
      preset: 'text',
      type: 'input',
      placeholder: 'Schedule Description',
      visible: (ctx: SingleCollection) =>
        isBank(ctx) &&
        (ctx.ScheduleType === 'recurring' ||
          (ctx.TakeFromAccountOn !== '' &&
            !vp.IsTodayDate(moment(Number(ctx.TakeFromAccountOn)).format('YYYY-MM-DD')))) &&
        ((Country === CA && ctx.Currency === CAD) || (Country === US && ctx.Currency === USD)),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.Message,
      id: vp.SingleCollectionId.Message,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => isInterac(ctx),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.SecCode,
      id: vp.SingleCollectionId.SecCode,
      require: () => false,
      preset: 'text',
      type: 'select',
      placeholder: 'Please Select...',
      visible: (ctx: SingleCollection) =>
        moreInfoNeeded && ctx.PaymentMethod === 'Bank Account' && ctx.BankingPaymentRail === 'ach',
      getSelectOptions: () => ({
        PPD: 'Prearranged Payment and Deposit',
        CCD: 'Corporate Credit or Debit',
        WEB: 'Internet-Initiated Entry',
        TEL: 'Telephone-Initiated Entry',
        ARC: 'Accounts Receivable Entry',
        IAT: 'International ACH Transaction',
        POP: 'Point-of-Purchase Entry',
      }),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.ClientReferenceNumber,
      id: vp.SingleCollectionId.ClientReferenceNumber,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      maxLength: 30,
      visible: () => moreInfoNeeded,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.TransactionLabel,
      id: vp.SingleCollectionId.TransactionLabel,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      maxLength: 30,
      visible: (ctx: SingleCollection) => TransactionLabelOverrideEnabled === '1' && isBank(ctx) && moreInfoNeeded,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.Notes,
      id: vp.SingleCollectionId.Notes,
      require: (ctx: SingleCollection) => ctx.ScheduleType === 'recurring',
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => (!isCreditCard(ctx) && moreInfoNeeded) || ctx.ScheduleType === 'recurring',
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.Nickname,
      id: vp.SingleCollectionId.Nickname,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => moreInfoNeeded && isCreditCard(ctx),
      disabled: () => false,
    },
  ];

  const contactDetails: ContactDetail[] = [
    {
      label: vp.SingleCollectionLabel.FirstName,
      id: vp.SingleCollectionId.FirstName,
      require: (ctx: SingleCollection) => (isProvideNew(ctx) || isRequestNew(ctx)) && ctx.ContactType === INDIVIDUAL,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => (isProvideNew(ctx) || isRequestNew(ctx)) && ctx.ContactType === INDIVIDUAL,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.LastName,
      id: vp.SingleCollectionId.LastName,
      require: (ctx: SingleCollection) => (isProvideNew(ctx) || isRequestNew(ctx)) && ctx.ContactType === INDIVIDUAL,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => (isProvideNew(ctx) || isRequestNew(ctx)) && ctx.ContactType === INDIVIDUAL,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.CompanyName,
      id: vp.SingleCollectionId.CompanyName,
      require: (ctx: SingleCollection) => (isProvideNew(ctx) || isRequestNew(ctx)) && ctx.ContactType === BUSINESS,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => (isProvideNew(ctx) || isRequestNew(ctx)) && ctx.ContactType === BUSINESS,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.RecipientEmailAddress,
      id: vp.SingleCollectionId.RecipientEmailAddress,
      require: (ctx: SingleCollection) => isProvideNew(ctx) && isCreditCard(ctx),
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => isProvideNew(ctx) && isCreditCard(ctx),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.RecipientPhoneNumber,
      id: vp.SingleCollectionId.RecipientPhoneNumber,
      require: (ctx: SingleCollection) => !isRequestNew(ctx) && ctx.Country === US && isBank(ctx),
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => !isRequestNew(ctx) && ctx.Country === US && isBank(ctx),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.RecipientEmailAddress,
      id: vp.SingleCollectionId.RecipientEmailAddress,
      require: (ctx: SingleCollection) =>
        (ctx.Country === 'US' && isBank(ctx) && !isRequestNew(ctx)) ||
        (isRequestNew(ctx) && ctx.NotificationMethod === 'email'),
      preset: 'email',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) =>
        (ctx.Country === 'US' && isBank(ctx) && !isRequestNew(ctx)) || isRequestNew(ctx),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.RecipientPhoneNumber,
      id: vp.SingleCollectionId.ReceiverPhoneNumber,
      require: (ctx: SingleCollection) => isRequestNew(ctx) && ctx.NotificationMethod === 'sms',
      preset: 'email',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => isRequestNew(ctx),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.Nickname,
      id: vp.SingleCollectionId.Nickname,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => isInterac(ctx),
      disabled: () => false,
    },
  ];

  const addressDetail: AddressDetail[] = [
    {
      label: vp.SingleCollectionLabel.Address1,
      id: vp.SingleCollectionId.Address1,
      require: (ctx: SingleCollection) => (isProvideNew(ctx) && isBank(ctx)) || (Country === 'US' && isBank(ctx)),
      preset: 'text',
      type: 'input',
      getGoogleCountries: (ctx: SingleCollection) => ({ [ctx.Country || CA]: ctx.Country || CA }),
      placeholder: '',
      visible: (ctx: SingleCollection) => isBank(ctx),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.Address2,
      id: vp.SingleCollectionId.Address2,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => isBank(ctx),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.City,
      id: vp.SingleCollectionId.City,
      require: (ctx: SingleCollection) => (isProvideNew(ctx) && isBank(ctx)) || (Country === 'US' && isBank(ctx)),
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => isBank(ctx),
      disabled: () => false,
    },
    {
      label: Country === 'CA' ? vp.SingleCollectionLabel.Province : vp.SingleCollectionLabel.State,
      id: vp.SingleCollectionId.Province,
      require: (ctx: SingleCollection) => (isProvideNew(ctx) && isBank(ctx)) || (Country === 'US' && isBank(ctx)),
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => isBank(ctx),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.Country,
      id: vp.SingleCollectionId.Country,
      require: (ctx: SingleCollection) => (isProvideNew(ctx) && isBank(ctx)) || (Country === 'US' && isBank(ctx)),
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: SingleCollection) => isBank(ctx),
      getSelectOptions: () => vp.CANADA_US_COUNTRY_OPTION,
      disabled: () => false,
    },
    {
      label: Country === 'CA' ? vp.SingleCollectionLabel.PostalCode : vp.SingleCollectionLabel.ZipCode,
      id: vp.SingleCollectionId.PostalCode,
      require: (ctx: SingleCollection) => (isProvideNew(ctx) && isBank(ctx)) || (Country === 'US' && isBank(ctx)),
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => isBank(ctx),
      disabled: () => false,
    },
  ];

  const bankDetail: BankDetail[] = [
    {
      label: vp.SingleCollectionLabel.FinancialInstitutionNumber,
      id: vp.SingleCollectionId.FinancialInstitutionNumber,
      require: (ctx: SingleCollection) => isProvideNew(ctx),
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: () => Country === 'CA',
      getSelectOptions: () => FinancialInstitutions,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.BranchTransitNumber,
      id: vp.SingleCollectionId.BranchTransitNumber,
      require: (ctx: SingleCollection) => isProvideNew(ctx) || isBank(ctx),
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: () => Country === 'CA',
      getSelectOptions: () => financialInstitutionBranches,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.ABARoutingNumber,
      id: vp.SingleCollectionId.ABARoutingNumber,
      require: (ctx: SingleCollection) => isProvideNew(ctx) || isBank(ctx),
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: () => Country === US,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.AccountNumber,
      id: vp.SingleCollectionId.AccountNumber,
      require: (ctx: SingleCollection) => isProvideNew(ctx) || isBank(ctx),
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: () => true,
      disabled: () => false,
    },
  ];

  const interacDetails: InteracDetail[] = [
    {
      label: vp.SingleCollectionLabel.EtransferConnectionType,
      id: vp.SingleCollectionId.EtransferConnectionType,
      require: (ctx: SingleCollection) => isProvideNew(ctx) && isInterac(ctx),
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: () => true,
      getSelectOptions: () => etransferEmailConnectionOption,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.RecipientName,
      id: vp.SingleCollectionId.RecipientName,
      require: (ctx: SingleCollection) => isInterac(ctx),
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: () => true,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.RecipientPhoneNumber,
      id: vp.SingleCollectionId.RecipientPhoneNumber,
      require: (ctx: SingleCollection) => isInterac(ctx) && ctx.EtransferConnectionType === PHONE,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => ctx.EtransferConnectionType === PHONE,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.RecipientEmailAddress,
      id: vp.SingleCollectionId.RecipientEmailAddress,
      require: (ctx: SingleCollection) => isInterac(ctx) && ctx.EtransferConnectionType === EMAIL,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => ctx.EtransferConnectionType === EMAIL,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.Question,
      id: vp.SingleCollectionId.Question,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: () => true,
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.Answer,
      id: vp.SingleCollectionId.Answer,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: () => true,
      disabled: () => false,
    },
  ];

  const requestDetails: RequestDetail[] = [
    {
      label: vp.SingleCollectionLabel.SenderName,
      id: vp.SingleCollectionId.SenderName,
      require: (ctx: SingleCollection) => isPaymentInformationRequest(ctx) || isRequestNew(ctx),
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => isPaymentInformationRequest(ctx) || isRequestNew(ctx),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.AccountConnectionMethod,
      id: vp.SingleCollectionId.AccountConnectionMethod,
      require: () => false,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: SingleCollection) =>
        (isPaymentInformationRequest(ctx) || isRequestNew(ctx)) &&
        !isCreditCard(ctx) &&
        PaymentMethod !== INTERAC &&
        PaymentMethod !== GOOGLE_PAY,
      getMultiSelectOptions: (ctx: SingleCollection) => {
        let connectionMethods: ItemProps[] = [];
        if (
          ctx.BankingPaymentRail === 'ach' &&
          (isPaymentInformationRequest(ctx) || isRequestNew(ctx)) &&
          !isCreditCard(ctx) &&
          PaymentMethod !== INTERAC
        ) {
          let methods: string | string[] = [];
          if (ctx.AccountConnectionMethod) {
            methods = ctx.AccountConnectionMethod.split('|');
          }

          connectionMethods = AccountConnectionMethods.map((item) => ({
            ...item,
            checked: methods.includes(item.value),
          }));
        }
        return connectionMethods || [];
      },
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.MessageForRecipient,
      id: vp.SingleCollectionId.MessageForRecipient,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      tooltipMessage:
        'The payment/collection transaction will be initiated immediately after the user has connected their bank account.',
      visible: (ctx: SingleCollection) => isPaymentInformationRequest(ctx) || isRequestNew(ctx),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.InvoiceNumber,
      id: vp.SingleCollectionId.InvoiceNumber,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => isPaymentInformationRequest(ctx) || isRequestNew(ctx),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.RecipientNotifications,
      id: vp.SingleCollectionId.RecipientNotifications,
      require: () => false,
      preset: 'text',
      type: 'toggle',
      placeholder: '',
      tooltipMessage: 'Send eLinx notification to the Recipient',
      visible: (ctx: SingleCollection) => isPaymentInformationRequest(ctx) || isRequestNew(ctx),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.RecipientReminders,
      id: vp.SingleCollectionId.RecipientReminders,
      require: () => false,
      preset: 'text',
      type: 'toggle',
      placeholder: '',
      tooltipMessage: 'Send eLinx reminder to the Recipient i.e. Successful, Accepted, and Rejected.',
      visible: (ctx: SingleCollection) => isPaymentInformationRequest(ctx) || isRequestNew(ctx),
      disabled: () => false,
    },
    {
      label: vp.SingleCollectionLabel.ReminderFreqeuncy,
      id: vp.SingleCollectionId.ReminderFreqeuncy,
      require: () => false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: SingleCollection) => isPaymentInformationRequest(ctx) || isRequestNew(ctx),
      disabled: (ctx: SingleCollection) => !ctx.SendReminder,
    },
    {
      label: vp.SingleCollectionLabel.NotificationMethod,
      id: vp.SingleCollectionId.NotificationMethod,
      require: () => false,
      preset: 'text',
      type: 'radio',
      placeholder: '',
      getRadioOptions: (ctx: SingleCollection) => getNotificationMethodOptions(ctx.NotificationMethod || 'email'),
      visible: (ctx: SingleCollection) => isPaymentInformationRequest(ctx) || isRequestNew(ctx),
      disabled: (ctx: SingleCollection) => !ctx.SendNotification,
    },
  ];

  return [transactionDetails, contactDetails, addressDetail, bankDetail, interacDetails, requestDetails];

  function buildCreditCardsOptions(list: vp.ClientCreditCardLists[]) {
    return list.reduce((acc, data) => {
      return {
        ...acc,
        [data.CreditCardToken]: `${data.CreditCardHolderName} ${getMaskedNumber(data.CreditCardNumber)}`,
      };
    }, {});
  }

  function buildInteracEmailsOptions(lists: vp.InteracEmailList[]) {
    return lists.reduce(
      (accumulator, data) => ({
        ...accumulator,
        [data.Token]: `${data.RecipientName}  (${data.RecipientEmailAddress || data.RecipientPhoneNumber || ''})`,
      }),
      {},
    );
  }

  function buildClientAccountOptions(lists: vp.ClientAccount[]) {
    const options: SelectOption[] = [];
    /*
     * forEach was used instead of reduce because when the list got large around 30000 client
     * accounts the overhead of maintaining the accumulator caused significant performance issues
     * when compared to forEach
     */
    lists.forEach((data) => {
      if (data.IsActive && data.ClientType !== 'receive-only') {
        options.push({
          ...data,
          label: data.ClientName,
          value: data.ClientAccountID,
        });
      }
    });
    return options;
  }

  function getBalanceAccount(data: vp.ClientAccount) {
    const balanceAccountObject = Object.values(data.Balances || {}).find((balance) => balance.Currency === currency);
    return (balanceAccountObject?.Balance || 0) - (balanceAccountObject?.PendingBalance || 0);
  }

  function getMaskedNumber(num: string) {
    const lastFourDigits = num ? num.slice(-4) : '';
    return `***${lastFourDigits}`;
  }

  function isInterac(ctx: Ctx) {
    return ctx.PaymentMethod === INTERAC;
  }

  function isCreditCard(ctx: Ctx) {
    return ctx.PaymentMethod === CREDIT_CARD;
  }

  function isBank(ctx: Ctx) {
    return ctx.PaymentMethod === BANK;
  }

  function isProvideNew(ctx: Ctx) {
    return ctx.Contact === PROVIDE;
  }

  function isPaymentInformationRequest(ctx: Ctx) {
    return ctx.ProvideOrRequestPaymentInformation === 'request';
  }

  function isRequestNew(ctx: Ctx) {
    return ctx.Contact === 'request';
  }
};

function OptionLabel({
  clientName,
  currency,
  balanceAccount,
  focused,
}: {
  clientName: string;
  currency: string;
  balanceAccount: number;
  focused: boolean;
}) {
  return (
    <div className={[styles.optionLabel, focused ? styles.focused : ''].join(' ')}>
      <div className={styles.optionLabelContent}>
        <div>{clientName}</div>
        <div className={styles.formatAvailableBalance}>{`(Available Bal. ${currency} $${vp.formattedCurrency(
          balanceAccount.toString(),
        )})`}</div>
      </div>
    </div>
  );
}
